import type { LocaleCode } from "@/i18n/locales";
import { assetUrl } from "@/utils/assets";
import type { PitangoMarketCommon } from "pitango-market-common";

type ProductPromotion = PitangoMarketCommon.DTO.ProductPromotionView;

/**
 * Product shape consumed by UI components. The real catalog API
 * (`@/services/catalog/catalog-api`) already localizes `name`/`description`
 * server-side for the requested language, so there is a single string per
 * field rather than a `content` map keyed by locale. `price` is `null` when
 * the server has no online price for the current audience (e.g. guest
 * pricing not configured) - components must handle that case explicitly
 * instead of assuming every product is purchasable.
 */
export interface Product {
    id: string;
    slugUrl: string | null;
    image: string;
    price: number | null;
    promotions: ProductPromotion[];
    brand?: string;
    categoryId?: string;
    categories?: Array<{ id: string; name: string }>;
    bestSellerRank?: number;
    badge?: "pitango" | "popular" | "200g";
    name: string;
    description: string;
}

/**
 * Offline/placeholder catalog data. Used as `initialData` for the products
 * query so the storefront renders instantly before the real API responds,
 * and as a fallback if the request fails. Kept in the legacy per-locale
 * `content` shape and adapted at the call site since it predates the real
 * API integration and is easiest to keep authoring in every locale here.
 */
export interface LocalizedProductContent { name: string; description: string; }
interface MockProduct {
    id: string;
    slugUrl: string | null;
    image: string;
    price: number;
    brand: string;
    categoryId: string;
    bestSellerRank?: number;
    badge?: "pitango" | "popular" | "200g";
    content: Record<LocaleCode, LocalizedProductContent>;
}

export function toProduct(mock: MockProduct, locale: LocaleCode): Product {
    const content = mock.content[locale];
    return { id: mock.id, slugUrl: mock.slugUrl, image: mock.image, price: mock.price, promotions: [], brand: mock.brand, categoryId: mock.categoryId, categories: [{ id: mock.categoryId, name: mock.brand }], bestSellerRank: mock.bestSellerRank, badge: mock.badge, name: content.name, description: content.description };
}

export function calculateProductTotal(product: Product, quantity: number): { total: number; savings: number; promotion: ProductPromotion | null } {
    const regularTotal = Number(((product.price ?? 0) * quantity).toFixed(2));
    const eligible = product.promotions.filter(promotion => promotion.isActive && quantity >= promotion.minimumBuyQuantity);
    const candidates = eligible.map(promotion => {
        if (promotion.discountType === "Percent") {
            return { promotion, total: Number((regularTotal * Math.max(0, 1 - promotion.discountValue / 100)).toFixed(2)) };
        }
        const bundleCount = Math.floor(quantity / promotion.minimumBuyQuantity);
        const remainder = quantity - bundleCount * promotion.minimumBuyQuantity;
        return { promotion, total: Number((bundleCount * promotion.discountValue + remainder * (product.price ?? 0)).toFixed(2)) };
    });
    const best = candidates.filter(candidate => candidate.total < regularTotal).sort((left, right) => left.total - right.total)[0];
    return best ? { total: best.total, savings: Number((regularTotal - best.total).toFixed(2)), promotion: best.promotion } : { total: regularTotal, savings: 0, promotion: null };
}

export function fallbackProducts(locale: LocaleCode): Product[] {
    return mockProducts.map(mock => toProduct(mock, locale));
}

const mockProducts: MockProduct[] = [
    {
        id: "ovaltine", slugUrl: "ovaltine-3-in-1", image: assetUrl("products/ovaltine.svg"), price: 39, brand: "Ovaltine", categoryId: "beverages", bestSellerRank: 2, badge: "pitango",
        content: {
            en: { name: "Ovaltine 3-in-1", description: "Ovaltine 3-in-1 malt chocolate drink mix." },
            he: { name: "אובלטין 3 ב-1", description: "משקה לתת ושוקולד אובלטין 3 ב-1." },
            th: { name: "โอวัลติน 3 อิน 1", description: "เครื่องดื่มมอลต์ช็อกโกแลตโอวัลตินแบบ 3 อิน 1" }
        }
    },
    {
        id: "alban", slugUrl: "alban-milk-powder-800g", image: assetUrl("products/alban.svg"), price: 55, brand: "Alban", categoryId: "beverages", bestSellerRank: 4,
        content: {
            en: { name: "Alban Milk Powder 800g", description: "Alban instant milk powder, 800g pack." },
            he: { name: "אבקת חלב אלבן 800 גרם", description: "אבקת חלב אינסטנט אלבן באריזת 800 גרם." },
            th: { name: "นมผง Alban 800 กรัม", description: "นมผงสำเร็จรูป Alban ขนาด 800 กรัม" }
        }
    },
    {
        id: "elite", slugUrl: "elite-turkish-coffee-100g", image: assetUrl("products/elite.svg"), price: 16.9, brand: "Elite", categoryId: "beverages", bestSellerRank: 3,
        content: {
            en: { name: "Elite Turkish Coffee 100g", description: "Elite roasted and ground Turkish coffee, 100g." },
            he: { name: "קפה טורקי עלית 100 גרם", description: "קפה טורקי קלוי וטחון של עלית, 100 גרם." },
            th: { name: "กาแฟตุรกี Elite 100 กรัม", description: "กาแฟตุรกีคั่วบด Elite ขนาด 100 กรัม" }
        }
    },
    {
        id: "oolong", slugUrl: "original-oolong-tea", image: assetUrl("products/oolong.svg"), price: 33, brand: "Singha Park", categoryId: "beverages", bestSellerRank: 5,
        content: {
            en: { name: "Original Oolong Tea", description: "Singha Park Chiang Rai original oolong tea." },
            he: { name: "תה אולונג אוריגינל", description: "תה אולונג מקורי מסינגה פארק צ'יאנג ראי." },
            th: { name: "ชาอู่หลงออริจินัล", description: "ชาอู่หลงสูตรดั้งเดิมจากสิงห์ปาร์ค เชียงราย" }
        }
    },
    {
        id: "jacobs-intense", slugUrl: "jacobs-intense-instant-coffee", image: assetUrl("products/jacobs-intense.svg"), price: 39.9, brand: "Jacobs", categoryId: "beverages", bestSellerRank: 1, badge: "popular",
        content: {
            en: { name: "Jacobs Intense Instant Coffee", description: "Jacobs Intense instant coffee with a rich, full-bodied profile." },
            he: { name: "ג'ייקובס קפה נמס אינטנס", description: "קפה נמס ג'ייקובס אינטנס בטעם עשיר ומלא." },
            th: { name: "Jacobs Intense Instant Coffee", description: "กาแฟสำเร็จรูป Jacobs Intense รสเข้มเต็มกลิ่น" }
        }
    },
    {
        id: "jacobs-gold", slugUrl: "jacobs-freeze-dried-coffee-200g", image: assetUrl("products/jacobs-gold.svg"), price: 38, brand: "Jacobs", categoryId: "beverages", bestSellerRank: 6, badge: "200g",
        content: {
            en: { name: "Jacobs Freeze-Dried Coffee 200g", description: "Jacobs freeze-dried instant coffee, 200g jar." },
            he: { name: "ג'ייקובס קפה נמס מיובש 200 גרם", description: "קפה נמס מיובש בהקפאה של ג'ייקובס, צנצנת 200 גרם." },
            th: { name: "กาแฟฟรีซดราย Jacobs 200 กรัม", description: "กาแฟสำเร็จรูปฟรีซดราย Jacobs ขนาด 200 กรัม" }
        }
    }
];
